import React, { useContext, useState } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Links from '../../common/Widgets/Links';
import { SiteContext } from '../../../context/SiteContext';
import SimpleDateInput from '../../common/DateInput/SimpleDateInput';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { PageAbsolutePath } from '../../../consts/Common';

type OrderVerificationFields = {
  orderReference: string;
  vrn: string;
  deliveryDate: Date | undefined;
};

type OrderVerificationErrors = {
  orderReference: string;
  vrn: string;
  deliveryDate: string;
};

const initialFormValues: OrderVerificationFields = {
  orderReference: '',
  vrn: '',
  deliveryDate: undefined
};

const initialFormErrors: OrderVerificationErrors = {
  orderReference: '',
  vrn: '',
  deliveryDate: ''
};

const OrderVerification = (): JSX.Element => {
  const navigate = useNavigate();
  const { site } = useContext(SiteContext);
  const { brand } = site;
  const [formValues, setFormValues] =
    useState<OrderVerificationFields>(initialFormValues);
  const [errors, setErrors] =
    useState<OrderVerificationErrors>(initialFormErrors);

  const { orderReference, vrn, deliveryDate } = formValues;

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  const goBack = (): void => {
    navigate(PageAbsolutePath.Insurance.AlternateEligibility);
  };

  const onNext = (): void => {
    if (validate()) {
      navigate(PageAbsolutePath.Insurance.AboutYou);
    }
  };

  const onDateChange = (date: Date | null) => {
    const updatedFormValues = {
      ...formValues,
      deliveryDate: date === null ? undefined : date
    };
    setFormValues(updatedFormValues);
  };

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };
    setFormValues(updatedFormValues);
  };

  const validatePolestarOrderReference = (): string => {
    const trimmedReference = orderReference.replace(/\s/g, '');
    const errorMessage = 'Order reference is in the wrong format';

    if (trimmedReference.length !== 8) {
      return errorMessage;
    }

    if (trimmedReference.substring(0, 2) !== '11') {
      return errorMessage;
    }

    const numericCharactersRegex = /^\d+$/;
    const result = numericCharactersRegex.test(trimmedReference);

    if (!result) {
      return errorMessage;
    }

    return '';
  };

  const validateGenesisOrderReference = (): string => {
    const trimmedReference = orderReference.replace(/\s/g, '');
    const errorMessage = 'Order reference is in the wrong format';

    if (trimmedReference.length < 8 || trimmedReference.length > 15) {
      return errorMessage;
    }

    const alphanumericCharactersRegex = /^[a-z0-9]+$/i;
    const result = alphanumericCharactersRegex.test(trimmedReference);

    if (!result) {
      return errorMessage;
    }

    return '';
  };

  const validate = (): boolean => {
    let isValid = true;

    const updatedFormErrors = { ...initialFormErrors };

    if (!orderReference) {
      isValid = false;
      updatedFormErrors.orderReference = 'Order reference is required';
    } else {
      if (brand.brandName.toLowerCase() === 'polestar') {
        const polestarError = validatePolestarOrderReference();
        isValid = polestarError ? false : true;
        updatedFormErrors.orderReference = polestarError;
      } else if (brand.brandName.toLowerCase() === 'genesis') {
        const genesisError = validateGenesisOrderReference();
        isValid = genesisError ? false : true;
        updatedFormErrors.orderReference = genesisError;
      }
    }

    if (!vrn) {
      isValid = false;
      updatedFormErrors.vrn = 'Vehicle registration is required';
    }

    if (!deliveryDate || !CommonValidation.isValidDate(deliveryDate, false)) {
      updatedFormErrors.deliveryDate = 'Date is required';
      isValid = false;
    }

    setErrors(updatedFormErrors);

    return isValid;
  };

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);

  return (
    <div>
      <Links
        id='back-link'
        title=''
        items={[
          { to: PageAbsolutePath.Insurance.AlternateEligibility, text: 'Back' }
        ]}
        icon={chevronLeftComponent}
      />
      <p>
        Please enter the following details to proceed with your application:
      </p>
      <div className='table-flex-layout'>
        <div className='form-container'>
          <div className='one-col'>
            <div className='control-label'>{`${brand.brandDisplayName} order reference`}</div>
            <div className='control'>
              <TextInput
                name='order-reference'
                placeholder='Order reference'
                value={orderReference}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'orderReference')
                }
                error={errors.orderReference}
              />
            </div>
          </div>
          <div className='one-col'>
            <div className='control-label'>
              What is your vehicle registration?
            </div>
            <div className='control'>
              <TextInput
                name='vrn'
                placeholder='Vehicle registration'
                value={vrn}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'vrn')
                }
                error={errors.vrn}
              />
            </div>
          </div>
          <div className='one-col'>
            <div className='control-label'>Vehicle delivery date</div>
            <div className='control'>
              <SimpleDateInput
                name='delivery-date'
                value={deliveryDate}
                error={errors.deliveryDate}
                onDateChange={onDateChange}
                minimumDate={minDate}
                maximumDate={maxDate}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btnNext'
          text='Next'
          onClick={onNext}
          additionalClassNames='btn-continue'
        />
      </div>
    </div>
  );
};

export default OrderVerification;
